/**
 * SHA-256 hashing for the match parameters (`em`, `ph`) used by Enhanced
 * Conversions and the platform Conversions APIs.
 *
 * Raw PII must never reach the dataLayer. Everything here hashes at the call
 * site; if hashing is unavailable we send nothing rather than falling back to
 * plaintext. The platform tags must be configured with "data is already
 * hashed" = true so the values are not hashed a second time.
 */

/** crypto.subtle only exists in a secure context (https, or localhost in dev). */
const canHash = (): boolean =>
  typeof window !== "undefined" &&
  !!window.crypto &&
  !!(window.crypto as any).subtle &&
  typeof TextEncoder !== "undefined";

const sha256 = async (value: string): Promise<string | undefined> => {
  if (!canHash()) return undefined;
  try {
    const bytes = new TextEncoder().encode(value);
    const digest = await window.crypto.subtle.digest("SHA-256", bytes);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return undefined;
  }
};

export const hashEmail = async (email?: string): Promise<string | undefined> => {
  const normalized = (email || "").trim().toLowerCase();
  if (!normalized || normalized.indexOf("@") === -1) return undefined;
  return sha256(normalized);
};

/**
 * E.164 without the leading `+`, e.g. "966555701856". A stray leading zero or a
 * missing country code silently drops the match, so both are stripped here
 * rather than at the ~5 call sites that collect a phone number.
 */
export const hashPhone = async (
  countryCode?: string,
  number?: string
): Promise<string | undefined> => {
  const cc = (countryCode || "").replace(/\D/g, "");
  const local = (number || "").replace(/\D/g, "").replace(/^0+/, "");
  if (!local) return undefined;
  const e164 = local.startsWith(cc) && cc ? local : cc + local;
  if (e164.length < 8) return undefined;
  return sha256(e164);
};

export interface UserData {
  em?: string;
  ph?: string;
}

/** Returns only the fields that actually hashed — never a raw value. */
export const buildUserData = async (input: {
  email?: string;
  countryCode?: string;
  phone?: string;
}): Promise<UserData> => {
  const [em, ph] = await Promise.all([
    hashEmail(input.email),
    hashPhone(input.countryCode, input.phone),
  ]);
  const userData: UserData = {};
  if (em) userData.em = em;
  if (ph) userData.ph = ph;
  return userData;
};
